/**
 * The four commands on `state.sharedRecipes` (PLAN-COLLAB A2, D-CO3, D-CO7).
 *
 * Three of them move a recipe across the line between a workspace and a room,
 * and each does it in one reducer so there is never a state in between: a
 * recipe that has left `recipes` but has no reference yet would vanish from the
 * list for as long as the gap lasted, and a copy without its repointed batches
 * would leave the brew log pointing at a room the brewer can no longer open.
 *
 * Those three answer no inverse, and that is the design rather than a gap. Each
 * follows a change in D1 membership the Worker has already made; undoing one
 * here would put the workspace out of step with who is actually in the room,
 * and the client never issues them anyway (`SERVER_ONLY_COMMANDS`).
 * `sharedRecipe.setEquipmentOverride` is an ordinary client edit and undoes like
 * one.
 *
 * None of the four writes a recipe document: `recipe.share` removes one and
 * `sharedRecipe.leave` makes a fresh one, but neither is an edit on a recipe's
 * timeline, which is why `recipeScopeOf` answers `null` for all of them.
 */

import { keepResolvableIngredientLinks, type SharedRecipeRef } from '../../document/shared-recipes';
import type { WorkspaceState } from '../../document/workspace';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import * as p from '../payloads';

/** A copy of `state` with one reference replaced (or added). */
function putRef(state: WorkspaceState, recipeId: string, ref: SharedRecipeRef): WorkspaceState {
  return { ...state, sharedRecipes: { ...state.sharedRecipes, [recipeId]: ref } };
}

/**
 * Point every recipe back-link held elsewhere in the workspace at `to`.
 *
 * Two collections hold a recipe id: `batches` (`batch.recipeId`) and
 * `recipeSnapshots` (`snapshot.recipeId`). A journal holds none: its recipe
 * block names a *batch* (`batch-recipe.batchId`) and reads the batch live, so
 * repointing the batch repoints the block. Blends and the cupboard link to
 * batches and ingredients, never to recipes.
 *
 * `updatedAt` is left alone on everything repointed, as `customIngredient.update`
 * leaves it on the lots it relabels: the brewer did not edit the batch. A
 * collection with nothing to repoint is returned by reference.
 */
function repointRecipe(state: WorkspaceState, from: string, to: string): WorkspaceState {
  let batches = state.batches;
  for (const [id, batch] of Object.entries(state.batches)) {
    if (batch.recipeId !== from) continue;
    if (batches === state.batches) batches = { ...state.batches };
    batches[id] = { ...batch, recipeId: to };
  }
  let recipeSnapshots = state.recipeSnapshots;
  for (const [id, snapshot] of Object.entries(state.recipeSnapshots)) {
    if (snapshot.recipeId !== from) continue;
    if (recipeSnapshots === state.recipeSnapshots) recipeSnapshots = { ...state.recipeSnapshots };
    recipeSnapshots[id] = { ...snapshot, recipeId: to };
  }
  return batches === state.batches && recipeSnapshots === state.recipeSnapshots
    ? state
    : { ...state, batches, recipeSnapshots };
}

/**
 * Move a recipe of this workspace into a room, as its owner.
 *
 * Its named versions go with it: the Worker has copied them into the room's
 * history (D-CO2), and a snapshot left behind here would be a second history of
 * a recipe this workspace no longer holds. Batches keep their back-link, since
 * the id lives on as the reference's key. Nothing else is touched.
 */
export const recipeShare = defineCommand({
  type: 'recipe.share',
  payload: p.recipeSharePayloadSchema,
  apply: (state, command) => {
    const { recipeId, equipmentOverrideId, joinedAt } = command.payload;
    const meta = { commandId: command.commandId, commandType: command.type };
    if (state.sharedRecipes[recipeId] !== undefined) {
      return {
        ok: false,
        error: commandError('invalid-state', `Recipe "${recipeId}" is already shared.`, meta),
      };
    }
    if (state.recipes[recipeId] === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No recipe with id "${recipeId}".`, meta),
      };
    }
    const recipes = { ...state.recipes };
    delete recipes[recipeId];
    const recipeSnapshots = Object.fromEntries(
      Object.entries(state.recipeSnapshots).filter(
        ([, snapshot]) => snapshot.recipeId !== recipeId,
      ),
    );
    return {
      ok: true,
      state: putRef({ ...state, recipes, recipeSnapshots }, recipeId, {
        role: 'owner',
        equipmentOverrideId,
        joinedAt,
      }),
    };
  },
  invert: () => null,
  summarize: (state, command) =>
    `Share recipe ${quoted(state.recipes[command.payload.recipeId]?.name ?? command.payload.recipeId)}`,
});

/** Add a reference to a room this workspace was let into, as an editor on the shared default. */
export const sharedRecipeJoin = defineCommand({
  type: 'sharedRecipe.join',
  payload: p.sharedRecipeJoinPayloadSchema,
  apply: (state, command) => {
    const { recipeId, joinedAt } = command.payload;
    if (state.recipes[recipeId] !== undefined || state.sharedRecipes[recipeId] !== undefined) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `This workspace already has a recipe with id "${recipeId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    return {
      ok: true,
      state: putRef(state, recipeId, { role: 'editor', equipmentOverrideId: null, joinedAt }),
    };
  },
  invert: () => null,
  summarize: () => 'Join a shared recipe',
});

/**
 * Drop a reference, and with `copy` keep the recipe as an ordinary one (D-CO7).
 *
 * The copy takes the new id, the leaver's own equipment where it still exists
 * here (else `null`, which reads as the workspace default), and only the
 * ingredient links this workspace can resolve. Every batch and named version
 * that pointed at the shared id is repointed at the copy, so the brew log goes
 * on naming a recipe the brewer can open.
 */
export const sharedRecipeLeave = defineCommand({
  type: 'sharedRecipe.leave',
  payload: p.sharedRecipeLeavePayloadSchema,
  apply: (state, command) => {
    const { recipeId, copy } = command.payload;
    const meta = { commandId: command.commandId, commandType: command.type };
    const ref = state.sharedRecipes[recipeId];
    if (ref === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No shared recipe with id "${recipeId}".`, meta),
      };
    }
    const sharedRecipes = { ...state.sharedRecipes };
    delete sharedRecipes[recipeId];
    const left: WorkspaceState = { ...state, sharedRecipes };
    if (copy === null) return { ok: true, state: left };

    const { newRecipeId } = copy;
    if (
      state.recipes[newRecipeId] !== undefined ||
      state.sharedRecipes[newRecipeId] !== undefined
    ) {
      return {
        ok: false,
        error: commandError('conflict', `A recipe with id "${newRecipeId}" already exists.`, meta),
      };
    }
    const override = ref.equipmentOverrideId;
    const equipmentProfileId =
      override !== null && state.equipmentProfiles[override] !== undefined ? override : null;
    const recipe = keepResolvableIngredientLinks(
      { ...copy.recipe, id: newRecipeId, equipmentProfileId },
      state.customIngredients,
    );
    return {
      ok: true,
      state: repointRecipe(
        { ...left, recipes: { ...left.recipes, [newRecipeId]: recipe } },
        recipeId,
        newRecipeId,
      ),
    };
  },
  invert: () => null,
  summarize: (_state, command) =>
    command.payload.copy === null
      ? 'Leave a shared recipe'
      : `Leave ${quoted(command.payload.copy.recipe.name)} and keep a copy`,
});

/**
 * Choose my own equipment for a shared recipe (D-CO3), or `null` for the
 * shared default. The profile has to be one of this workspace's: the room's
 * profiles are not mine to point at.
 */
export const sharedRecipeSetEquipmentOverride = defineCommand({
  type: 'sharedRecipe.setEquipmentOverride',
  payload: p.sharedRecipeSetEquipmentOverridePayloadSchema,
  apply: (state, command) => {
    const { recipeId, equipmentProfileId } = command.payload;
    const meta = { commandId: command.commandId, commandType: command.type };
    const ref = state.sharedRecipes[recipeId];
    if (ref === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No shared recipe with id "${recipeId}".`, meta),
      };
    }
    if (equipmentProfileId !== null && state.equipmentProfiles[equipmentProfileId] === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No equipment profile with id "${equipmentProfileId}".`,
          meta,
        ),
      };
    }
    return {
      ok: true,
      state: putRef(state, recipeId, { ...ref, equipmentOverrideId: equipmentProfileId }),
    };
  },
  // A reference carries no `updatedAt`, so the inverse rides on the command's
  // own time; nothing is stamped from it.
  invert: (state, command) => {
    const prior = state.sharedRecipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'sharedRecipe.setEquipmentOverride',
          { recipeId: command.payload.recipeId, equipmentProfileId: prior.equipmentOverrideId },
          command.timestamp,
        );
  },
  summarize: (state, command) => {
    const id = command.payload.equipmentProfileId;
    return id === null
      ? 'Use the shared default equipment'
      : `Use ${quoted(state.equipmentProfiles[id]?.name ?? id)} just for me`;
  },
});
